import { getTheme, IButtonStyles, IFontStyles, IStackStyles, ITextStyles } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPFontIcon, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { editIcon, globalStackTokensGapExtraSmall } from 'globalStyles';
import RecurringPattern, { absoluteYearCycle, relativeYearCycle } from 'models/recurringPattern';
import { useContext, useMemo } from 'react';
import { hasUserFeatureGenericManager } from 'services/Auth/featurePermissions';
import { RecurringYearSelector } from './RecurringYearSelector';

export enum RecurringPatternSummaryStyle {
  text = 1,
  interactive = 2,
  icon = 3,
  edit = 4,
  button = 5,
}

interface IRecurringSummaryProps {
  pattern: RecurringPattern | undefined;
  onChange?: (year: number, checked: boolean) => void;
  summaryStyle: RecurringPatternSummaryStyle;
  variant?: keyof IFontStyles;
  stackStyles?: IStackStyles;
  textStyles?: ITextStyles;
  buttonStyles?: IButtonStyles;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  onClick?: () => void;
}

export const RecurringSummary = (props: IRecurringSummaryProps) => {
  const appContext = useContext(AppContext);
  const isManager = useMemo(() => hasUserFeatureGenericManager(appContext), [appContext]);
  const theme = getTheme();

  //
  // Render
  //
  if (!props.pattern) {
    return null;
  }

  if (props.summaryStyle === RecurringPatternSummaryStyle.text) {
    return (
      <IPText styles={props.textStyles} variant={props.variant}>
        {props.pattern.getSummary()}
      </IPText>
    );
  }

  if (props.summaryStyle === RecurringPatternSummaryStyle.icon) {
    return (
      <IPTooltipHost content={props.pattern.getSummary()}>
        <IPFontIcon iconName="Sync" />
      </IPTooltipHost>
    );
  }

  if (props.summaryStyle === RecurringPatternSummaryStyle.edit) {
    return (
      <IPTextField
        placeholder={props.placeholder}
        disabled={props.disabled || !isManager}
        readOnly
        label={props.label}
        value={props.pattern ? props.pattern.getSummary() : ''}
        onClick={props.onClick}
        onKeyDown={(e) => {
          if (props.onClick && (e.code === 'Enter' || e.code === 'Space')) {
            props.onClick();
          }
        }}
        styles={{ field: { cursor: props.disabled ? 'default' : 'pointer' } }}
        onRenderSuffix={() => {
          return (
            <IPFontIcon
              style={{ color: theme.palette.themePrimary, cursor: props.disabled ? 'default' : 'pointer' }}
              {...editIcon}
              onClick={props.onClick}
            ></IPFontIcon>
          );
        }}
      ></IPTextField>
    );
  }

  if (props.summaryStyle === RecurringPatternSummaryStyle.button) {
    return (
      <IPDefaultButton disabled={props.disabled || !isManager} styles={props.buttonStyles} onClick={props.onClick}>
        {props.pattern.getShortSummary()}
      </IPDefaultButton>
    );
  }

  if (props.summaryStyle === RecurringPatternSummaryStyle.interactive) {
    if (props.pattern.repeatInterval === absoluteYearCycle || props.pattern.repeatInterval === relativeYearCycle) {
      return (
        <IPStack styles={props.stackStyles} tokens={globalStackTokensGapExtraSmall}>
          <IPStack.Item>
            <IPText variant={props.variant}>{props.pattern.getShortSummary()}</IPText>
          </IPStack.Item>
          <IPStack.Item>
            <RecurringYearSelector
              disabled={props.disabled || !isManager}
              pattern={props.pattern}
              onChange={props.onChange}
            />
          </IPStack.Item>
        </IPStack>
      );
    }
  }

  return null;
};
